import { AvailableExtra, Currency } from '~/lib/types/basket-types';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { toDisplayCurrency } from '~/lib/prices';
import { P2GText } from '@parcel2go/core-ui';
import { Protection } from '~/lib/types/extra-types';
import Price from '~/components/core/price/price';
import { QuoteIncludedCoverButParcelValueIsHigher, IsSpecialQuotePageScenario } from './is-special-quote-scenario';

type props = {
  isCheapestOption: boolean;
  currency: Currency;
  extra: AvailableExtra;
  parcelValue: number;
  serviceIncludedProtection: number | undefined;
  maxProtection: number;
  maxAmount: number;
};

export const ProtectionMessage = ({
  currency,
  extra,
  serviceIncludedProtection,
  parcelValue,
  isCheapestOption,
  maxAmount
}: props) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const includedProtection = serviceIncludedProtection || 0;
  const protectedAmount = extra.metadata?.protectedAmount || 0;
  const baseSelectedAndDoesntFullyProtect = IsSpecialQuotePageScenario(isCheapestOption, extra, parcelValue, maxAmount);
  const includedDoesntFullyProtect = QuoteIncludedCoverButParcelValueIsHigher(
    isCheapestOption,
    extra,
    parcelValue,
    includedProtection
  );

  // This would only be the 0 index if it was the 'No' option.
  if (baseSelectedAndDoesntFullyProtect || includedDoesntFullyProtect) {
    return (
      <P2GText>
        <Trans
          i18nKey="PROTECTION_INCLUDED_NO_MESSAGE"
          t={t}
          defaults="No, I'll accept the risk with the standard {{includedProtection}} protection."
          values={{
            includedProtection: toDisplayCurrency(
              currency,
              baseSelectedAndDoesntFullyProtect ? maxAmount : includedProtection,
              0
            )
          }}
          components={[<P2GText dataTestId="included-protection-amount" />]}
        />
      </P2GText>
    );
  }

  if (includedProtection === 0 && extra.key === Protection.NONE) {
    return <P2GText>{t('PROTECTION_NO_MESSAGE', { defaultValue: "No, don't protect my parcel" })}</P2GText>;
  }

  if (extra.key !== Protection.NONE && parcelValue > protectedAmount) {
    return (
      <P2GText fontWeight="bold">
        <Trans
          i18nKey="PROTECTION_YES_MESSAGE"
          t={t}
          defaults="Protect my parcel upto <strong>{{protectionAmount}}</strong> for <price />."
          values={{
            protectionAmount: toDisplayCurrency(currency, protectedAmount, 0),
            price: toDisplayCurrency(currency, extra.price.net, 0)
          }}
          components={{
            price: (
              <Price
                excVat={!!extra.price.tax}
                fontWeight="bold"
                currency={currency}
                value={extra.price.net}
                dataTestId="PROTECTION_YES_PRICE"
              />
            ),
            strong: <P2GText fontWeight="bold" dataTestId="protection-amount" />
          }}
        />
      </P2GText>
    );
  }

  if (extra.key !== Protection.NONE && parcelValue <= protectedAmount) {
    return (
      <P2GText fontWeight="bold">
        <Trans
          i18nKey="PROTECTION_YES_FULLY_MESSAGE"
          t={t}
          defaults="Fully protect my parcel for <price />."
          values={{
            protectionAmount: toDisplayCurrency(currency, protectedAmount, 0),
            price: toDisplayCurrency(currency, extra.price.net, 0)
          }}
          components={{
            price: (
              <Price
                excVat={!!extra.price.tax}
                fontWeight="bold"
                currency={currency}
                value={extra.price.net}
                dataTestId="PROTECTION_YES_FULLY_PRICE"
              />
            )
          }}
        />
      </P2GText>
    );
  }

  return null;
};
